/**
 * ApplicationManagementModule
 *
 * Manages Forge application lifecycle for a use case:
 * - Create application if none exists (push to Forge)
 * - Associate an existing Forge application (filtered by domain forge)
 * - Show application status and actions if exists
 * - Trigger builds
 * - Delete application
 */
import { AppWindow, Link, Rocket, TriangleAlert } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import { Button } from '../Common/button';
import notify from '@/lib/notify';
import OneApplicationModule from './OneApplicationModule';
import { ProgressStepper, ProgressStep } from '../Common/ProgressStepper';
import { ApplicationSelector } from './ApplicationSelector';
import { ApplicationCreationWizard } from './ApplicationCreationWizard';

interface ApplicationManagementModuleProps {
  useCaseId: string;
  useCaseName: string;
  applicationId?: string | null;
  /** forge_id of the domain linked to this use case — used to filter existing apps */
  domainForgeId?: string | null;
  onApplicationCreated?: (appId: string) => void;
  onApplicationDeleted?: () => void;
  currentUserEmail: string;
  hideUseCaseLink?: boolean;
  cadrageId?: string | null;
  cadrageName?: string | null;
  /** Optional cadrage description, used to prefill the wizard. */
  cadrageDescription?: string | null;
  /** Called after the use case (and its cadrage) has been unlinked from the application. */
  onApplicationUnlinked?: () => void;
}

export const ApplicationManagementModule: React.FC<ApplicationManagementModuleProps> = ({
  useCaseId,
  useCaseName,
  applicationId,
  domainForgeId,
  onApplicationCreated,
  onApplicationDeleted,
  currentUserEmail,
  hideUseCaseLink = false,
  cadrageId,
  cadrageName,
  cadrageDescription,
  onApplicationUnlinked,
}) => {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const [showWizard, setShowWizard] = useState(false);
  const [showAssociate, setShowAssociate] = useState(false);
  const [selectedAssocAppId, setSelectedAssocAppId] = useState<string | null>(null);
  const [isAssociating, setIsAssociating] = useState(false);

  // Step-based progress states (build + delete only — creation runs inside the wizard)
  const [buildStep, setBuildStep] = useState<number>(-1);
  const [deleteStep, setDeleteStep] = useState<number>(-1);
  const [hasBuildError, setHasBuildError] = useState(false);
  const [hasDeleteError, setHasDeleteError] = useState(false);

  const isBuilding = buildStep >= 0;
  const isDeleting = deleteStep >= 0;

  const buildSteps: ProgressStep[] = [
    { id: 'queue', label: t('forge.progress.queuing', 'Queuing') },
    { id: 'build', label: t('forge.progress.building', 'Building') },
    { id: 'finalize', label: t('forge.progress.finalizing', 'Finalizing') },
    { id: 'done', label: t('forge.progress.done', 'Done') },
  ];

  const deleteSteps: ProgressStep[] = [
    { id: 'stop', label: t('forge.progress.stopping', 'Stopping') },
    { id: 'remove', label: t('forge.progress.removing', 'Removing') },
    { id: 'done', label: t('forge.progress.done', 'Done') },
  ];

  const advanceStepRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const simulateSteps = (
    totalSteps: number,
    setStep: (n: number) => void,
  ): Promise<void> => {
    return new Promise((resolve) => {
      let current = 0;
      setStep(0);
      const stepDuration = 600;
      advanceStepRef.current = setInterval(() => {
        current += 1;
        if (current >= totalSteps - 1) {
          if (advanceStepRef.current) { clearInterval(advanceStepRef.current); advanceStepRef.current = null; }
          resolve();
        } else {
          setStep(current);
        }
      }, stepDuration);
    });
  };

  useEffect(() => {
    return () => {
      if (advanceStepRef.current) clearInterval(advanceStepRef.current);
    };
  }, []);

  const handleTriggerBuild = async () => {
    if (!applicationId) return;
    setHasBuildError(false);

    try {
      const stepsPromise = simulateSteps(buildSteps.length, setBuildStep);
      await Promise.all([
        forgeService.triggerApplicationBuild(applicationId, {
          branch: 'main',
          triggered_by: currentUserEmail,
        }),
        stepsPromise,
      ]);

      setBuildStep(buildSteps.length - 1);
      await new Promise((r) => setTimeout(r, 500));
      notify.success(t('forge.application.buildInitialized', 'Build initialized'));
    } catch (error: any) {
      console.error('Failed to trigger build:', error);
      setHasBuildError(true);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.buildError', `Failed to trigger build: ${errorMessage}`));
      await new Promise((r) => setTimeout(r, 1500));
    } finally {
      if (advanceStepRef.current) { clearInterval(advanceStepRef.current); advanceStepRef.current = null; }
      setBuildStep(-1);
      setHasBuildError(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!applicationId) return;
    setHasDeleteError(false);

    try {
      const stepsPromise = simulateSteps(deleteSteps.length, setDeleteStep);
      await Promise.all([
        forgeService.deleteApplication(applicationId),
        stepsPromise,
      ]);

      setDeleteStep(deleteSteps.length - 1);
      await new Promise((r) => setTimeout(r, 500));
      notify.success(t('forge.application.deleteSuccess', 'Application deleted successfully'));
      setShowDeleteConfirm(false);
      onApplicationDeleted?.();
    } catch (error: any) {
      console.error('Failed to delete application:', error);
      setHasDeleteError(true);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.deleteError', `Failed to delete application: ${errorMessage}`));
      await new Promise((r) => setTimeout(r, 1500));
    } finally {
      if (advanceStepRef.current) { clearInterval(advanceStepRef.current); advanceStepRef.current = null; }
      setDeleteStep(-1);
      setHasDeleteError(false);
    }
  };

  const handleUnlinkApp = async () => {
    if (!useCaseId) return;
    setIsUnlinking(true);
    try {
      await forgeService.mapUseCaseToApplication(useCaseId, null);
      notify.success(t('forge.application.unlinkSuccess', 'Application unlinked successfully'));
      setShowUnlinkConfirm(false);
      onApplicationUnlinked?.();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.unlinkError', `Failed to unlink application: ${msg}`));
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleAssociateApp = async () => {
    if (!selectedAssocAppId) return;
    try {
      setIsAssociating(true);
      await forgeService.mapUseCaseToApplication(useCaseId, selectedAssocAppId);
      notify.success(t('forge.application.associateSuccess', 'Application associated successfully'));
      onApplicationCreated?.(selectedAssocAppId);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      notify.error(t('forge.application.associateError', `Failed to associate application: ${msg}`));
    } finally {
      setIsAssociating(false);
    }
  };

  // Shared wizard — rendered alongside the active branch so both states can open it.
  const wizard =
    domainForgeId && showWizard ? (
      <ApplicationCreationWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        mode={{
          kind: 'use_case',
          useCaseId,
          useCaseName,
          forgeId: domainForgeId,
          description: cadrageDescription ?? null,
        }}
        currentUserEmail={currentUserEmail}
        onCreated={({ id }) => onApplicationCreated?.(id)}
      />
    ) : null;

  // No application - show create / associate options
  if (!applicationId) {
    return (
      <div className="space-y-4">
        {wizard}
        <div className="bg-muted/30 border border-border rounded-xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <AppWindow className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="text-lg font-medium text-foreground mb-1">
              {t('forge.application.noApplication', 'No Forge Application')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t(
                'forge.application.noApplicationDescription',
                'Create a new application in Forge or associate an existing one to deploy this solution.'
              )}
            </p>
          </div>

          {/* No forge linked to domain — warning only, no action buttons */}
          {!domainForgeId ? (
            <div className="flex items-start gap-3 max-w-md mx-auto rounded-lg border border-amber-400/50 bg-amber-400/10 px-4 py-3">
              <TriangleAlert className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {t('forge.application.associateNoDomain', 'No Forge is linked to this domain. Associate a Forge with the domain first.')}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  onClick={() => { setShowWizard(true); setShowAssociate(false); }}
                  disabled={isAssociating}
                  className="gap-2"
                >
                  <Rocket className="h-4 w-4" />
                  {t('forge.application.pushToForge', 'Create an application')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowAssociate((v) => !v); setSelectedAssocAppId(null); }}
                  className="gap-2"
                >
                  <Link className="h-4 w-4" />
                  {t('forge.application.modeAssociate', 'Associate existing app')}
                </Button>
              </div>

              {/* Inline associate form — expands below the buttons */}
              {showAssociate && (
                <div className="mt-5 max-w-md mx-auto space-y-3 border-t border-border pt-5 animate-fade-in">
                  <p className="text-sm text-muted-foreground">
                    {t('forge.application.associateDescription', 'Select an existing application from the Forge linked to this domain.')}
                  </p>
                  <ApplicationSelector
                    forgeId={domainForgeId}
                    selectedApplicationId={selectedAssocAppId}
                    onSelect={(appId) => setSelectedAssocAppId(appId)}
                    disabled={isAssociating}
                  />
                  <div className="flex gap-3">
                    <Button
                      onClick={handleAssociateApp}
                      disabled={!selectedAssocAppId || isAssociating}
                      className="gap-2"
                    >
                      <Link className="h-4 w-4" />
                      {isAssociating ? t('global.saving', 'Saving…') : t('forge.application.associate', 'Associate')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setShowAssociate(false); setSelectedAssocAppId(null); }}
                    >
                      {t('global.cancel', 'Cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Application exists - show management UI
  return (
    <div className="space-y-6">
      {/* Build progress */}
      {isBuilding && (
        <div className="border border-border rounded-lg p-4 bg-muted/20 animate-fade-in">
          <p className="text-sm font-medium text-foreground mb-1">
            {t('forge.progress.buildInProgress', 'Build in progress...')}
          </p>
          <ProgressStepper steps={buildSteps} currentStep={buildStep} hasError={hasBuildError} />
        </div>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="p-4 border border-destructive bg-destructive/10 rounded-lg animate-fade-in">
          <div className="flex items-start gap-3">
            <TriangleAlert className="h-6 w-6 text-destructive flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">
                {t('forge.application.deleteConfirmTitle', 'Delete Application?')}
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  'forge.application.deleteConfirmMessage',
                  'This will permanently delete the application from Forge. This action cannot be undone.'
                )}
              </p>
              {isDeleting ? (
                <ProgressStepper steps={deleteSteps} currentStep={deleteStep} hasError={hasDeleteError} />
              ) : (
                <div className="flex gap-3">
                  <Button
                    onClick={handleDeleteApplication}
                    disabled={isDeleting}
                    variant="destructive"
                    size="sm"
                  >
                    {t('forge.application.confirmDelete', 'Yes, Delete')}
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(false)}
                    variant="outline"
                    size="sm"
                  >
                    {t('global.cancel', 'Cancel')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unlink confirmation dialog */}
      {showUnlinkConfirm && (
        <div className="p-4 border border-amber-400/50 bg-amber-400/10 rounded-lg animate-fade-in">
          <div className="flex items-start gap-3">
            <TriangleAlert className="h-6 w-6 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">
                {t('forge.application.unlinkConfirmTitle', 'Unlink application?')}
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t(
                  'forge.application.unlinkConfirmMessage',
                  'This will detach this Forge application from the use case (and its solution). The application itself will not be deleted.'
                )}
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleUnlinkApp}
                  disabled={isUnlinking}
                  size="sm"
                >
                  {isUnlinking
                    ? t('forge.application.unlinking', 'Unlinking…')
                    : t('forge.application.confirmUnlink', 'Yes, unlink')}
                </Button>
                <Button
                  onClick={() => setShowUnlinkConfirm(false)}
                  disabled={isUnlinking}
                  variant="outline"
                  size="sm"
                >
                  {t('global.cancel', 'Cancel')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application details */}
      <OneApplicationModule
        applicationId={applicationId}
        useCaseId={useCaseId}
        useCaseName={useCaseName}
        cadrageId={cadrageId}
        cadrageName={cadrageName}
        hideUseCaseLink={hideUseCaseLink}
        showReadme={true}
        onBuild={handleTriggerBuild}
        onDelete={() => setShowDeleteConfirm(true)}
        onUnlinkCadrage={() => setShowUnlinkConfirm(true)}
        isBuilding={isBuilding}
        isDeleting={isDeleting}
        isUnlinking={isUnlinking}
      />
    </div>
  );
};

export default ApplicationManagementModule;
